import type { RestockFinding } from '@dca/shared';

import { DataTable } from '../components/DataTable.tsx';
import { ItemLabel } from '../components/ItemLabel.tsx';
import { Badge, Card, Empty, Notice, Spinner } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/auth.tsx';
import { number, relativeTime } from '../lib/format.ts';
import { ErrorState } from './Upgrade.tsx';

export function RestockPage() {
  const restock = useAsync(() => api.insights.restock(), []);

  if (restock.loading) return <Spinner />;
  if (restock.error) return <ErrorState error={restock.error} />;

  const findings = restock.data!.findings;
  const stockouts = findings.filter((f) => f.status === 'likely_stockout');
  const slowing = findings.filter((f) => f.status === 'slowing');

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Restock &amp; stockouts</h1>
          <p>
            We never see your chest contents - the API reports money, not inventory. These findings
            infer stock from the shape of your sales: an item that reliably sold every few hours and
            has gone quiet at a competitive price is almost certainly empty.
          </p>
        </div>
      </header>

      {findings.length === 0 ? (
        <Empty title="Every item is selling on its usual cadence">
          <span className="muted">
            Nothing has gone quiet long enough to look like a stockout.
          </span>
        </Empty>
      ) : (
        <>
          <Card
            title={`${stockouts.length} likely stockout${stockouts.length === 1 ? '' : 's'}`}
            subtitle="Silent for several times the normal gap, while still priced competitively"
          >
            {stockouts.length === 0 ? (
              <Empty title="No likely stockouts" />
            ) : (
              <FindingTable findings={stockouts} tone="critical" />
            )}
          </Card>

          <Card
            title={`${slowing.length} slowing`}
            subtitle="Cadence is slipping - either stock is running low or the price has drifted"
          >
            {slowing.length === 0 ? (
              <Empty title="Nothing is slowing down" />
            ) : (
              <FindingTable findings={slowing} tone="warning" />
            )}
          </Card>
        </>
      )}

      <div style={{ marginTop: 16 }}>
        <Notice>
          <strong>Why some quiet items are not flagged.</strong> If an item stopped selling while
          priced above market, that is a pricing problem, not an empty chest - we say so rather than
          sending you to refill a chest that is already full.
        </Notice>
      </div>
    </>
  );
}

function FindingTable({
  findings,
  tone,
}: {
  findings: RestockFinding[];
  tone: 'critical' | 'warning';
}) {
  return (
    <DataTable
      rows={findings}
      rowKey={(finding) => `${finding.itemCode}-${finding.lastSaleAt}`}
      initialSort={{ key: 'stock', dir: 'asc' }}
      searchPlaceholder="Search items"
      pageSize={25}
      columns={[
        {
          key: 'item',
          header: 'Item',
          searchText: (f) => `${f.displayName} ${f.itemCode}`,
          sortValue: (f) => f.displayName,
          render: (f) => (
            <ItemLabel
              itemKey={f.itemCode}
              material={f.itemCode}
              displayName={f.displayName}
              size={20}
            />
          ),
        },
        {
          key: 'status',
          header: 'Status',
          sortValue: (f) => f.status,
          render: () => (
            <Badge tone={tone}>{tone === 'critical' ? 'likely stockout' : 'slowing'}</Badge>
          ),
        },
        {
          key: 'stock',
          header: 'Stock',
          align: 'num',
          sortValue: (f) => f.currentStock ?? null,
          render: (f) =>
            f.currentStock == null ? <span className="muted">unknown</span> : number(f.currentStock),
        },
        {
          key: 'evidence',
          header: 'Evidence',
          sortValue: (f) => f.source,
          render: (f) => (
            <span className="small muted">
              {f.source === 'observed_stock' ? 'observed stock' : 'inferred from sales'}
            </span>
          ),
        },
        {
          key: 'lastSale',
          header: 'Last sale',
          align: 'num',
          sortValue: (f) => f.hoursSinceLastSale,
          render: (f) => relativeTime(f.lastSaleAt),
        },
        {
          key: 'velocity',
          header: 'Velocity',
          align: 'num',
          sortValue: (f) => f.velocityPerDay,
          render: (f) => `${number(f.velocityPerDay)}/day`,
        },
        {
          key: 'why',
          header: 'Why',
          searchText: (f) => f.reason,
          render: (f) => (
            <span className="small muted" style={{ display: 'block', maxWidth: 380 }}>
              {f.reason}
            </span>
          ),
        },
      ]}
    />
  );
}
